import { defineStore } from 'pinia';
import { watch } from 'vue';

import type { UserResultDTO } from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';
import {
  PositionResponse,
  TrackApiWebClient,
} from '@nora-monorepo/track-api-web-client';
import {
  NoraWebViewEventPattern,
  TrackingUtilities,
  useAuthStore,
  useConfigStore,
  useGenericUserStore,
  useWebViewStore,
} from '@nora-monorepo/ui-shared';

import { useOperationStore } from '#nora-ui/stores/operation-store';
import { TracksManager } from '#nora-ui/utils/tracks-manager';

/**
 * Represents a partial or complete position update from the native layer.
 * Only `producerId` is required - all other fields are optional for partial updates.
 */
type PartialPositionUpdate = Partial<PositionResponse> & { producerId: string };

/**
 * Type guard to check if incoming data has a valid producerId.
 */
function hasValidProducerId(data: { producerId?: string }): data is PartialPositionUpdate {
  return typeof data.producerId === 'string' && (data.producerId).length > 0;
}

/**
 * Validates that incoming position data has the minimum required fields
 * to create a new track (when no existing track exists to merge with).
 */
function isCompletePosition(data: PartialPositionUpdate): boolean {
  return typeof data.latitude === 'number' && typeof data.longitude === 'number';
}

/**
 * Merges an incoming partial position update with an existing position.
 * Only fields explicitly present in the incoming data will overwrite existing values.
 * Fields not present in the incoming data will preserve their existing values.
 * A value of 0 is treated as a valid value (not "missing").
 */
function mergePositionUpdate(
  existing: PositionResponse,
  incoming: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...existing };

  for (const key of Object.keys(incoming)) {
    if (key in incoming) {
      merged[key] = incoming[key];
    }
  }

  return merged;
}

export type TrackListener = (track: PositionResponse) => void;

export const useTracksStore = defineStore('tracks', () => {
  const webViewStore = useWebViewStore();
  const userStore = useGenericUserStore();
  const authStore = useAuthStore();
  const configStore = useConfigStore();
  const operationStore = useOperationStore();

  let tracksManager: TracksManager | undefined;
  let isInitialized = false;
  const listeners = new Set<TrackListener>();

  function addListener(listener: TrackListener): () => void {
    listeners.add(listener);

    // Return unsubscribe function
    return () => {
      listeners.delete(listener);
    };
  }

  function removeListener(listener: TrackListener): void {
    listeners.delete(listener);
  }

  function handleIncomingTrack(track: PositionResponse): void {
    const producerId = track.producerId;

    if (userStore.user?.id === producerId) {
      userStore.updateLastTrack(track);
    }

    // Notify all listeners
    listeners.forEach((listener) => {
      try {
        listener(track);
      }
      catch (error) {
        console.error('[TracksStore] Error in track listener:', error);
      }
    });

    if (TrackingUtilities.isTrackerPosition(track)) {
      operationStore.handleIncomingTrackerTrack(producerId, track);
    }
    else {
      operationStore.handleIncomingUserTrack(producerId, track);
    }
  }

  function initialize(): void {
    if (isInitialized) {
      console.warn('[TracksStore] Already initialized, skipping initialization.');

      return;
    }

    const user: UserResultDTO | undefined = userStore.user;

    if (!user) {
      console.warn('[TracksStore] No user available, cannot initialize tracks.');

      return;
    }

    const grpcServerUrl = configStore.get('trackApi.webUrl');

    if (!isNonEmptyString(grpcServerUrl)) {
      console.warn('[TracksStore] No track API URL configured, cannot initialize tracks.');

      return;
    }

    if (webViewStore.isWebView) {
      webViewStore.addNativeLayerEventListener(
        NoraWebViewEventPattern.TRACK_API_STREAM_POSITION_RESPONSE,
        (event: CustomEvent<{ data: Record<string, unknown> }>) => {
          const incomingData = event.detail.data;

          if (!hasValidProducerId(incomingData)) {
            console.warn('[TracksStore] Received position update without valid producerId:', incomingData);
            return;
          }

          const { producerId } = incomingData;
          const existingTrack = userStore.user?.lastTrack;

          if (userStore.isCurrentUser(producerId) && existingTrack) {
            // Merge partial update with existing position for current user
            const existingPosition = TrackingUtilities.trackDTOToPositionResponse(existingTrack);
            const merged = mergePositionUpdate(existingPosition, incomingData);

            handleIncomingTrack(PositionResponse.create(merged));
          }
          else if (isCompletePosition(incomingData)) {
            // No existing data - only process if it's a complete position
            handleIncomingTrack(PositionResponse.create(incomingData));
          }
          else {
            // Incomplete position with nothing to merge - log warning and ignore
            console.warn(
              '[TracksStore] Received incomplete position update with no existing track to merge:',
              incomingData,
            );
          }
        },
      );
    }
    else {
      const client = new TrackApiWebClient(grpcServerUrl);

      tracksManager = new TracksManager(
        client,
        handleIncomingTrack,
      );

      tracksManager.start();
    }

    // Watch for auth token changes and restart the tracks manager
    watch(() => authStore.token, () => {
      try {
        tracksManager?.restart();
      }
      catch (error) {
        console.error('[TracksStore] Error restarting TracksManager on auth token change:', error);
      }
    });

    isInitialized = true;
  }

  function cleanup(): void {
    tracksManager?.stop();
    tracksManager = undefined;
    isInitialized = false;
    listeners.clear();
  }

  return {
    initialize,
    cleanup,
    handleIncomingTrack,
    addListener,
    removeListener,
  };
});
